import React, { useEffect, useRef, useState } from "react";
import { hslToHex, isValidHex } from "../../../../../../utils/color";
import "./ColorPicker.css";

/* ---------- Generate structured palette at module level ---------- */

const HUES = [0, 30, 55, 120, 175, 220, 275, 330];
const LIGHTNESS_STEPS = [75, 62, 50, 40, 30, 20];
const SATURATION = 80;

const CHROMATIC: string[] = [];
for (const l of LIGHTNESS_STEPS) {
  for (const h of HUES) {
    CHROMATIC.push(hslToHex(h, SATURATION, l));
  }
}

const NEUTRALS = [
  "#ffffff", "#d4d4d4", "#a0a0a0", "#737373",
  "#525252", "#3f3f3f", "#262626", "#000000",
];

/* ---------- Component ---------- */

interface ColorPickerProps {
  value: string;
  onChange: (hex: string) => void;
  /** Colors already taken by other trades — shown dimmed and non-selectable. */
  usedColors?: Set<string>;
}

const ColorPicker: React.FC<ColorPickerProps> = ({ value, onChange, usedColors }) => {
  const safeValue = value || "#000000";
  const [hexText, setHexText] = useState(value);
  const nativeRef = useRef<HTMLInputElement>(null);

  // Sync hexText when value changes externally (e.g. switching edit target)
  useEffect(() => { setHexText(value); }, [value]);

  const commitHex = () => {
    const v = hexText.trim().toLowerCase();
    if (isValidHex(v)) {
      onChange(v);
    } else {
      setHexText(value);
    }
  };

  const handleHexKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") commitHex();
  };

  const handleNativeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const hex = e.target.value;
    onChange(hex);
    setHexText(hex);
  };

  const handleSwatchClick = (hex: string) => {
    onChange(hex);
    setHexText(hex);
  };

  const isUsed = (c: string) => usedColors != null && usedColors.has(c.toLowerCase());

  const renderSwatch = (c: string, key: string | number) => {
    const taken = isUsed(c);
    const selected = value.toLowerCase() === c.toLowerCase();
    let cls = "color-picker__swatch";
    if (selected) cls += " color-picker__swatch--selected";
    if (taken) cls += " color-picker__swatch--used";
    return (
      <button
        key={key}
        className={cls}
        style={{ backgroundColor: c }}
        onClick={taken ? undefined : () => handleSwatchClick(c)}
        disabled={taken}
        title={taken ? "Already in use" : c}
        type="button"
      />
    );
  };

  return (
    <div>
      <div className="color-picker__grid">
        {CHROMATIC.map((c, i) => renderSwatch(c, i))}
      </div>
      <div className="color-picker__divider" />
      <div className="color-picker__grid">
        {NEUTRALS.map((c) => renderSwatch(c, c))}
      </div>

      <div className="color-picker__custom-row">
        <button
          className="color-picker__custom-btn"
          onClick={() => nativeRef.current?.click()}
          type="button"
        >
          <span
            className="color-picker__custom-btn-swatch"
            style={{ backgroundColor: safeValue }}
          />
          Pick Custom Color
        </button>
        <input
          type="color"
          ref={nativeRef}
          className="color-picker__native-hidden"
          value={safeValue}
          onChange={handleNativeChange}
        />
        <input
          type="text"
          className="color-picker__hex-input"
          value={hexText}
          onChange={(e) => setHexText(e.target.value)}
          onBlur={commitHex}
          onKeyDown={handleHexKeyDown}
          maxLength={7}
          placeholder="#000000"
        />
      </div>
    </div>
  );
};

export default ColorPicker;
